/**
 * Home Page — War Table
 *
 * Main hub for authenticated users. Dark atmospheric theme with
 * Compass Rose background, lateral ember drift, and scanning glow border.
 */

import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import { OnlinePlayersList } from '../components/OnlinePlayersList';
import { PlayerProfileModal } from '../components/PlayerProfileModal';
import { TradeModal } from '../components/TradeModal';
import { CommunityBattleFeed } from '../components/CommunityBattleFeed';
import { PageAtmosphere } from '../components/atmosphere';
import type { OnlinePlayer } from '@drawn-of-war/shared';
import styles from './HomePage.module.css';
import '../styles/atmosphere/pages/home.css';

/**
 * SVG Icons for quick actions
 */
function BattleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 17.5L3 6V3h3l11.5 11.5" />
      <path d="M13 19l6-6" />
      <path d="M16 16l4 4" />
      <path d="M19 21l2-2" />
      <path d="M14.5 6.5L18 3h3v3l-3.5 3.5" />
      <path d="M5 14l4 4" />
      <path d="M7 17l-3 3" />
    </svg>
  );
}

function CreateIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </svg>
  );
}

interface UserStats {
  wins: number;
  losses: number;
  disconnects: number;
  totalBattles: number;
  currentWinStreak: number;
  bestWinStreak: number;
}

interface UserProfile {
  displayName: string;
  email: string;
  stats: UserStats;
}

export function HomePage() {
  const { user, getIdToken } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState<OnlinePlayer | null>(null);
  const [showTradeModal, setShowTradeModal] = useState(false);

  const handlePlayerClick = useCallback((player: OnlinePlayer) => {
    setSelectedPlayer(player);
  }, []);

  const handleCloseProfile = useCallback(() => {
    setSelectedPlayer(null);
  }, []);

  const handleOpenTrade = useCallback(() => {
    setShowTradeModal(true);
  }, []);

  const handleCloseTrade = useCallback(() => {
    setShowTradeModal(false);
  }, []);

  useEffect(() => {
    async function fetchProfile() {
      try {
        const token = await getIdToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

        const response = await fetch(`${apiUrl}/api/users/me/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setProfile(data);
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [getIdToken]);

  const stats = profile?.stats || {
    wins: 0,
    losses: 0,
    disconnects: 0,
    totalBattles: 0,
    currentWinStreak: 0,
    bestWinStreak: 0
  };

  const winRate = stats.totalBattles > 0
    ? ((stats.wins / stats.totalBattles) * 100).toFixed(1)
    : '0.0';

  return (
    <PageAtmosphere page="home">
      <div className={styles.container}>
        {/* War Table Header */}
        <div className={styles.warHeader}>
          <div className={styles.warTitle}>War Table</div>
          <div className={styles.warSubtitle}>
            Welcome, {user?.displayName || 'Commander'}. Survey your forces and plan your next engagement.
          </div>
          <div className={styles.warDivider} />
        </div>

        {/* Stats Banner */}
        {loading ? (
          <div className={styles.loadingState}>Loading stats...</div>
        ) : stats.totalBattles === 0 ? (
          <div className={styles.emptyState}>
            <p>Your battle statistics will appear here after your first match.</p>
            <p>Ready to prove yourself?</p>
            <Link to="/battles" className={styles.ctaButton}>
              <BattleIcon />
              Enter the Battle Lobby
            </Link>
          </div>
        ) : (
          <div className={styles.statsBanner}>
            <div className={styles.statCell}>
              <div className={`${styles.statValue} ${styles.gold}`}>{stats.wins}</div>
              <div className={styles.statLabel}>Victories</div>
            </div>
            <div className={styles.statCell}>
              <div className={`${styles.statValue} ${styles.vermillion}`}>{stats.losses}</div>
              <div className={styles.statLabel}>Defeats</div>
            </div>
            <div className={styles.statCell}>
              <div className={styles.statValue}>{winRate}%</div>
              <div className={styles.statLabel}>Win Rate</div>
            </div>
            <div className={styles.statCell}>
              <div className={`${styles.statValue} ${styles.gold}`}>{stats.currentWinStreak}</div>
              <div className={styles.statLabel}>Current Streak</div>
            </div>
            <div className={styles.statCell}>
              <div className={styles.statValue}>{stats.bestWinStreak}</div>
              <div className={styles.statLabel}>Best Streak</div>
            </div>
          </div>
        )}

        {/* Dashboard Grid */}
        <div className={styles.dashboardGrid}>
          {/* Quick Actions Card */}
          <div className={styles.dashCard}>
            <div className={styles.dashCardHeader}>
              <span className={styles.dashCardTitle}>Quick Actions</span>
            </div>
            <div className={styles.dashCardBody}>
              <div className={styles.quickActions}>
                <Link to="/battles" className={styles.quickAction}>
                  <span className={styles.quickActionIcon}><BattleIcon /></span>
                  <div className={styles.quickActionLabel}>Battle</div>
                  <div className={styles.quickActionDesc}>Find an opponent</div>
                </Link>
                <Link to="/create" className={styles.quickAction}>
                  <span className={styles.quickActionIcon}><CreateIcon /></span>
                  <div className={styles.quickActionLabel}>Create</div>
                  <div className={styles.quickActionDesc}>Summon a creature</div>
                </Link>
                <Link to="/challenges" className={styles.quickAction}>
                  <span className={styles.quickActionIcon}><TrophyIcon /></span>
                  <div className={styles.quickActionLabel}>Challenge</div>
                  <div className={styles.quickActionDesc}>Weekly trial</div>
                </Link>
                <Link to="/adventure" className={styles.quickAction}>
                  <span className={styles.quickActionIcon}><MapIcon /></span>
                  <div className={styles.quickActionLabel}>Adventure</div>
                  <div className={styles.quickActionDesc}>Explore the world</div>
                </Link>
              </div>
            </div>
          </div>

          {/* Community Battle Feed Card */}
          <div className={styles.dashCard}>
            <div className={styles.dashCardHeader}>
              <span className={styles.dashCardTitle}>Community Feed</span>
            </div>
            <div className={styles.dashCardBody}>
              <div className={styles.communityFeedWrapper}>
                <CommunityBattleFeed initialLimit={5} />
              </div>
            </div>
          </div>
        </div>

        {/* Players Panel */}
        <aside className={styles.onlinePlayersPanel}>
          <OnlinePlayersList
            showSearch={true}
            initialLimit={10}
            compact={true}
            showOffline={true}
            onPlayerClick={handlePlayerClick}
          />
        </aside>

        {/* Footer */}
        <div className={styles.warFooter}>
          <div className={styles.warFooterText}>The battlefield awaits your command.</div>
        </div>

        {/* Player Profile Modal */}
        {selectedPlayer && (
          <PlayerProfileModal
            userId={selectedPlayer.id}
            isOpen={true}
            onClose={handleCloseProfile}
            onTrade={handleOpenTrade}
          />
        )}

        {/* Trade Modal */}
        {selectedPlayer && showTradeModal && (
          <TradeModal
            isOpen={showTradeModal}
            onClose={handleCloseTrade}
            targetUserId={selectedPlayer.id}
            targetDisplayName={selectedPlayer.displayName}
          />
        )}
      </div>
    </PageAtmosphere>
  );
}
